import React, { useRef, useEffect, useState, useCallback, useMemo } from 'react';
import BlockRenderer, { BLOCK_REGISTRY, DEFAULT_LAYOUT, getBlockDef } from './BlockRenderer';
import { ReportFooter } from './ReportComponents';
import InlineBlockOverlay from './InlineBlockOverlay';
import FloatingToolbar from './FloatingToolbar';
import { GRID_COLS, GRID_ROWS } from './layoutUtils';
import { resolveColours } from './colourPalette';

/**
 * Detect which positioned blocks have content overflowing their container.
 */
function useOverflowDetection(pageLayout, data) {
  const [overflowSet, setOverflowSet] = useState(new Set());
  const blockRefs = useRef({});

  const registerBlockRef = useCallback((blockId, el) => {
    if (el) {
      blockRefs.current[blockId] = el;
    } else {
      delete blockRefs.current[blockId];
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      const newOverflow = new Set();
      for (const [blockId, el] of Object.entries(blockRefs.current)) {
        if (el && el.scrollHeight > el.clientHeight * 1.15) {
          newOverflow.add(blockId);
        }
      }
      setOverflowSet(newOverflow);
    }, 500);
    return () => clearTimeout(timer);
  }, [pageLayout, data]);

  return { overflowSet, registerBlockRef, blockRefs };
}

const ReportViewer = ({
  data,
  performanceChartRef,
  allocationVsSaaChartRef,
  activeTiltsChartRef,
  allocationDonutChartRef,
  historicalAllocationChartRef,
  creditChartRef,
  dynamicChartRefs,
  onOverflowChange,
  // Inline layout editing props
  layoutEditMode,
  onBlockChange,     // (pageIndex, blockId, updatedBlock) => void
  onBlocksChange,    // (pageIndex, {blockId: updatedBlock}) => void — batch update, one dispatch
  onRemoveBlock,     // (blockId) => void
  onAddBlock,        // (pageIndex) => void
  onToggleFooter,    // (pageIndex) => void — hide/show footer on a page
  onReorderBlock,    // (pageIndex, blockId, toFront) => void — layer order
  overflowBlocks,    // Set of block IDs
  customBlocks,      // custom block definitions
  // Inline text editing props
  editMode,          // true when in edit view mode
  onTextChange,      // (contentKey, html) => void
  onCustomTextChange, // (blockId, html) => void
  onHeadingChange,   // (blockId, text) => void
  onHeadingStyleChange, // (blockId, styles) => void
  onBlockDefChange,    // (blockId, blockDef) => void
}) => {
  if (!data) return null;

  const chartRefs = {
    performanceChartRef,
    allocationVsSaaChartRef,
    activeTiltsChartRef,
    allocationDonutChartRef,
    historicalAllocationChartRef,
    creditChartRef,
    dynamicChartRefs,
  };

  const pageLayout = data.content?.page_layout || DEFAULT_LAYOUT;
  const { overflowSet, registerBlockRef, blockRefs } = useOverflowDetection(pageLayout, data);

  // Refs for each page's canvas element (for coordinate conversion)
  const canvasRefs = useRef({});

  // Gridlines toggle — hidden by default
  const [showGridlines, setShowGridlines] = useState(false);

  // Floating toolbar state — which editor is currently focused
  const [activeEditor, setActiveEditor] = useState(null);
  const [activeAnchorEl, setActiveAnchorEl] = useState(null);

  const handleTextFocus = useCallback((blockId, editor, anchorEl) => {
    setActiveEditor(editor);
    setActiveAnchorEl(anchorEl);
  }, []);

  const handleTextBlur = useCallback((blockId) => {
    // Small delay to allow toolbar button clicks to register before hiding
    setTimeout(() => {
      setActiveEditor(prev => {
        // Only clear if still the same editor (another focus may have fired)
        return prev;
      });
    }, 200);
  }, []);

  // Clear floating toolbar when clicking outside any editor
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!activeEditor) return;
      const isToolbar = e.target.closest('.floating-toolbar');
      const isEditor = e.target.closest('.inline-text-editor-wrap');
      if (!isToolbar && !isEditor) {
        setActiveEditor(null);
        setActiveAnchorEl(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [activeEditor]);

  // Build editProps for BlockRenderer — active in edit mode (text editing coexists with layout mode)
  const editProps = useMemo(() => {
    if (!editMode) return null;
    return {
      editMode: true,
      onTextChange,
      onCustomTextChange,
      onHeadingChange,
      onHeadingStyleChange,
      onBlockDefChange,
      onTextFocus: handleTextFocus,
      onTextBlur: handleTextBlur,
    };
  }, [editMode, onTextChange, onCustomTextChange, onHeadingChange, onHeadingStyleChange, onBlockDefChange, handleTextFocus, handleTextBlur]);

  // Notify parent about overflow changes
  useEffect(() => {
    if (onOverflowChange) {
      onOverflowChange(overflowSet);
    }
  }, [overflowSet, onOverflowChange]);

  // Autofit all blocks on a page — temporarily unconstrain each block to measure content
  const handleAutofitAll = useCallback((pageIndex) => {
    const canvas = canvasRefs.current[pageIndex];
    if (!canvas || !onBlockChange) return;
    const canvasH = canvas.clientHeight;
    if (canvasH <= 0) return;
    const page = pageLayout[pageIndex];
    if (!page) return;

    // Measure every block's natural content height first, then reflow in
    // (y, x) order: a block that grew (e.g. a long holdings table) pushes the
    // blocks below it down rather than overlapping them, and heights are
    // clamped to the space left on the page so nothing hangs past the bottom
    // edge — content that still can't fit shows the OVERFLOW badge.
    const items = [];
    page.blocks.forEach(block => {
      if (typeof block === 'string') return;
      const el = blockRefs.current[block.id];
      if (!el) return;

      const origHeight = el.style.height;
      const origOverflow = el.style.overflow;
      el.style.height = 'auto';
      el.style.overflow = 'hidden';
      const contentH = el.scrollHeight;
      el.style.height = origHeight;
      el.style.overflow = origOverflow;

      const neededH = Math.min(GRID_ROWS, Math.max(1, Math.ceil(contentH / canvasH * GRID_ROWS)));
      items.push({ block, neededH });
    });

    items.sort((a, b) => (a.block.y - b.block.y) || (a.block.x - b.block.x));
    const placed = [];
    const updates = {};
    items.forEach(({ block, neededH }) => {
      const overlapsX = (p) => block.x < p.x + p.w && p.x < block.x + block.w;
      const floor = placed.filter(overlapsX).reduce((m, p) => Math.max(m, p.y + p.h), 0);
      const newY = Math.min(Math.max(block.y, floor), GRID_ROWS - 1);
      const newH = Math.max(1, Math.min(neededH, GRID_ROWS - newY));
      placed.push({ ...block, y: newY, h: newH });
      if (newY !== block.y || newH !== block.h) {
        updates[block.id] = { ...block, y: newY, h: newH };
      }
    });
    if (Object.keys(updates).length === 0) return;
    // One batched dispatch — sequential onBlockChange calls each rebuild the
    // page from a stale snapshot, so only the last block's change would stick.
    if (onBlocksChange) {
      onBlocksChange(pageIndex, updates);
    } else {
      Object.values(updates).forEach(b => onBlockChange(pageIndex, b.id, b));
    }
  }, [pageLayout, onBlockChange, onBlocksChange, blockRefs]);

  // Set CSS custom properties so var(--table-header-*) picks up branded colours
  const colours = resolveColours(data);
  const reportStyle = {
    '--table-header-primary': colours.table_header_primary,
    '--table-header-secondary': colours.table_header_secondary,
    '--table-row-band': colours.table_row_band,
    // Brand primary/secondary apply to the REPORT PAGES only, and only for
    // white-labelled domains — setting them any higher (the app root) tinted
    // the toolbar and sidebar chrome with the client's colours.
    '--primary-navy': (data.whitelabel && data.branding?.primary_color) || '#072040',
    '--secondary-gold': (data.whitelabel && data.branding?.secondary_color) || '#f0a500',
  };

  return (
    <div className="report-pages" data-report-ready="true" style={reportStyle}>
      {/* Floating toolbar for inline text editing */}
      {editProps && (
        <FloatingToolbar editor={activeEditor} anchorEl={activeAnchorEl} />
      )}

      {pageLayout.map((page, pageIndex) => {
        const firstBlock = page.blocks[0];
        const isPositioned = page.blocks.length === 0 || (firstBlock && typeof firstBlock === 'object' && firstBlock.id);

        return (
          <div key={pageIndex} className={`qr-page section-1 page-${pageIndex + 1}`}>
            {isPositioned ? (
              <div
                className={`qr-content-canvas ${layoutEditMode ? 'layout-edit-active' : ''}`}
                ref={(el) => { canvasRefs.current[pageIndex] = el; }}
              >
                {/* Grid guide in layout mode */}
                {layoutEditMode && showGridlines && (
                  <div className="inline-grid-guide">
                    {Array.from({ length: GRID_COLS }).map((_, i) => (
                      <div key={i} className="inline-grid-col" />
                    ))}
                  </div>
                )}

                {/* Top page controls */}
                {layoutEditMode && (
                  <div className="inline-page-controls top">
                    {onAddBlock && (
                      <button
                        className="inline-add-block-btn"
                        onClick={() => onAddBlock(pageIndex)}
                      >
                        + Add Block
                      </button>
                    )}
                    {page.blocks.length > 0 && (
                      <button
                        className="inline-autofit-all-btn"
                        onClick={() => handleAutofitAll(pageIndex)}
                      >
                        Autofit All
                      </button>
                    )}
                    <button
                      className={`inline-gridlines-btn${showGridlines ? ' active' : ''}`}
                      onClick={() => setShowGridlines(prev => !prev)}
                      title={showGridlines ? 'Hide gridlines' : 'Show gridlines'}
                    >
                      {showGridlines ? 'Hide Grid' : 'Show Grid'}
                    </button>
                  </div>
                )}

                {page.blocks.map(block => {
                  // Full-bleed blocks extend to the page edge on any side they're
                  // already flush with the content canvas (matches the cover image
                  // in the original). Page padding is 12.7mm sides/top, 18mm bottom.
                  const fb = block.fullBleed;
                  const blockStyle = fb ? {
                    position: 'absolute',
                    left: block.x <= 0 ? '-12.7mm' : `${(block.x / GRID_COLS * 100).toFixed(2)}%`,
                    top: block.y <= 0 ? '-12.7mm' : `${(block.y / GRID_ROWS * 100).toFixed(2)}%`,
                    right: (block.x + block.w) >= GRID_COLS ? '-12.7mm' : `${((GRID_COLS - (block.x + block.w)) / GRID_COLS * 100).toFixed(2)}%`,
                    bottom: (block.y + block.h) >= GRID_ROWS ? '-18mm' : `${((GRID_ROWS - (block.y + block.h)) / GRID_ROWS * 100).toFixed(2)}%`,
                  } : {
                    position: 'absolute',
                    left: `${(block.x / GRID_COLS * 100).toFixed(2)}%`,
                    top: `${(block.y / GRID_ROWS * 100).toFixed(2)}%`,
                    width: `${(block.w / GRID_COLS * 100).toFixed(2)}%`,
                    height: `${(block.h / GRID_ROWS * 100).toFixed(2)}%`,
                  };
                  return (
                  <div
                    key={block.id}
                    className={`qr-block-positioned ${layoutEditMode ? 'layout-editable' : ''}${fb ? ' qr-block-fullbleed' : ''}`}
                    ref={(el) => registerBlockRef(block.id, el)}
                    style={blockStyle}
                    onClick={(e) => {
                      if (!editMode) return;
                      // If click didn't land on an editor, focus the first TipTap editor in this block
                      if (!e.target.closest('.tiptap') && !e.target.closest('.inline-overlay-bar')) {
                        const tiptap = e.currentTarget.querySelector('.tiptap');
                        if (tiptap) tiptap.focus();
                      }
                    }}
                  >
                    <BlockRenderer
                      blockId={block.id}
                      data={data}
                      chartRefs={chartRefs}
                      editProps={editProps}
                    />
                    {layoutEditMode && onBlockChange && (
                      <InlineBlockOverlay
                        block={block}
                        pageIndex={pageIndex}
                        canvasEl={canvasRefs.current[pageIndex]}
                        blockEl={blockRefs.current[block.id]}
                        onBlockChange={(updated) => onBlockChange(pageIndex, block.id, updated)}
                        onRemoveBlock={onRemoveBlock}
                        onReorderBlock={onReorderBlock ? (toFront) => onReorderBlock(pageIndex, block.id, toFront) : undefined}
                        isOverflowing={overflowBlocks?.has(block.id) || overflowSet.has(block.id)}
                        customBlocks={customBlocks}
                        onBlockDefChange={editProps?.onBlockDefChange}
                      />
                    )}
                  </div>
                  );
                })}

                {/* Layout mode controls */}
                {layoutEditMode && (
                  <div className="inline-page-controls">
                    {onAddBlock && (
                      <button
                        className="inline-add-block-btn"
                        onClick={() => onAddBlock(pageIndex)}
                      >
                        + Add Block
                      </button>
                    )}
                    {page.blocks.length > 0 && (
                      <button
                        className="inline-autofit-all-btn"
                        onClick={() => handleAutofitAll(pageIndex)}
                      >
                        Autofit All
                      </button>
                    )}
                    <button
                      className={`inline-gridlines-btn${showGridlines ? ' active' : ''}`}
                      onClick={() => setShowGridlines(prev => !prev)}
                      title={showGridlines ? 'Hide gridlines' : 'Show gridlines'}
                    >
                      {showGridlines ? 'Hide Grid' : 'Show Grid'}
                    </button>
                    {onToggleFooter && (
                      <button
                        className={`inline-gridlines-btn${page.hideFooter ? ' active' : ''}`}
                        onClick={() => onToggleFooter(pageIndex)}
                        title={page.hideFooter ? 'Show footer on this page' : 'Hide footer on this page'}
                      >
                        {page.hideFooter ? 'Show Footer' : 'Hide Footer'}
                      </button>
                    )}
                  </div>
                )}
              </div>
            ) : (
              page.blocks.map(blockId => (
                <BlockRenderer
                  key={blockId}
                  blockId={blockId}
                  data={data}
                  chartRefs={chartRefs}
                  editProps={editProps}
                />
              ))
            )}
            {!page.hideFooter && (
              <ReportFooter quarterDisplay={data.quarter_display} quarter={data.quarter} page={pageIndex + 1} />
            )}
          </div>
        );
      })}

    </div>
  );
};

export default ReportViewer;
